import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

type Scheme = "light" | "dark";

const STORAGE_KEY = "a4b-color-scheme";
const SYNC_EVENT = "a4b-color-scheme-change";

export function getInitialScheme(): Scheme {
	const saved = localStorage.getItem(STORAGE_KEY);
	if (saved === "light" || saved === "dark") return saved;
	return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const VARS_STYLE_ID = "a4b-demo-theme-vars";

/**
 * Injeta (1×) as regras de `--bg`/`--text` keyed em `[data-theme]`. Antes estes
 * viviam como inline style no documentElement, mas inline vence qualquer regra de
 * stylesheet: quando o toolkit trocava o `data-theme` por dentro da reunião (seu
 * próprio toggle), o fundo ficava preso no valor do load — "só alguns elementos
 * trocavam". Como folha de estilo, quem controlar o `data-theme` (toolkit dentro
 * da reunião, Demo fora) dirige `--bg`/`--text` automaticamente.
 */
function ensureThemeVarsStylesheet(): void {
	if (document.getElementById(VARS_STYLE_ID)) return;
	const el = document.createElement("style");
	el.id = VARS_STYLE_ID;
	el.textContent = `:root[data-theme="light"]{--bg:#f8fafc;--text:#0f172a}:root[data-theme="dark"]{--bg:#0b1622;--text:#e8eef5}`;
	document.head.appendChild(el);
}

/**
 * Aplica o esquema no documentElement via `data-theme` (o toolkit deriva a paleta a
 * partir dele; as regras `[data-theme]` acima dão `--bg`/`--text` ao AppLoader antes
 * do bundle do toolkit montar). Fonte única de cor — sem inline pinning.
 */
export function applyThemeVars(scheme: Scheme): void {
	ensureThemeVarsStylesheet();
	// Remove qualquer inline legado de sessões/versões anteriores que venceria a folha.
	document.documentElement.style.removeProperty("--bg");
	document.documentElement.style.removeProperty("--text");
	document.documentElement.setAttribute("data-theme", scheme);
}

/**
 * Estado do tema claro/escuro compartilhado pelo Demo. Aplica `data-theme` no
 * documentElement (cobre harness + toolkit + modais portados em body), persiste
 * em localStorage e usa prefers-color-scheme como padrão inicial. Usado pelo
 * toggle flutuante global e pelo botão de tema do Centro de Controle.
 */
export function useColorScheme(): [Scheme, () => void] {
	const [scheme, setScheme] = useState<Scheme>(getInitialScheme);

	useEffect(() => {
		applyThemeVars(scheme);
		localStorage.setItem(STORAGE_KEY, scheme);
		window.dispatchEvent(new Event(SYNC_EVENT));
	}, [scheme]);

	// Instâncias coexistem montadas (toggle global + botão do Centro de
	// Controle); o evento propaga a troca para as demais via localStorage.
	useEffect(() => {
		const sync = () => setScheme(getInitialScheme());
		window.addEventListener(SYNC_EVENT, sync);
		return () => window.removeEventListener(SYNC_EVENT, sync);
	}, []);

	// Dentro da reunião o toggle do toolkit (ColorSchemeProvider) troca o
	// `data-theme` diretamente, sem passar pelo localStorage/SYNC_EVENT do Demo.
	// Observa essa troca para persistir a escolha e manter o toggle global em sync.
	useEffect(() => {
		const root = document.documentElement;
		const observer = new MutationObserver(() => {
			const applied = root.getAttribute("data-theme");
			if ((applied === "light" || applied === "dark") && applied !== localStorage.getItem(STORAGE_KEY)) {
				localStorage.setItem(STORAGE_KEY, applied);
				setScheme(applied);
			}
		});
		observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
		return () => observer.disconnect();
	}, []);

	const toggle = () => setScheme((s) => (s === "dark" ? "light" : "dark"));
	return [scheme, toggle];
}

/** Toggle flutuante global de tema claro/escuro do Demo. */
export default function ThemeToggle() {
	const { pathname } = useLocation();
	const [scheme, toggle] = useColorScheme();

	// Escondido na reunião (/meeting), que tem o próprio toggle na modal de
	// Configurações do toolkit, e no Centro de Controle, cujo toggle vive no
	// header do dashboard (o flutuante sobreporia o relógio).
	const hidden = pathname.startsWith("/dashboard") || pathname.startsWith("/admin") || pathname.startsWith("/meeting");
	if (hidden) return null;

	const isDark = scheme === "dark";

	return (
		<button
			type="button"
			className="demo-theme-toggle"
			onClick={toggle}
			aria-label={isDark ? "Ativar tema claro" : "Ativar tema escuro"}
			title={isDark ? "Tema claro" : "Tema escuro"}
		>
			{isDark ? (
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<circle cx="12" cy="12" r="5" />
					<path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
				</svg>
			) : (
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
				</svg>
			)}
		</button>
	);
}
